import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { AssetDto } from '@picflow/types';
import { ActivityGroup, ActivityItem } from '../../../interfaces/activity.interface';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { ActivityService } from '../../../services/activity.service';
import { AssetService } from '../../../services/asset.service';
import { OnGetData } from '../../../services/dialog.service';
import { ActivityBadgeComponent } from '../../../shared-extra/components/activity-badge/activity-badge.component';
import { LinkListItemComponent } from '../../../shared-extra/components/link-list-item/link-list-item.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { InViewDirective } from '../../../shared/directives/in-view.directive';
import { ScrollElementDirective } from '../../../shared/directives/scroll-element.directive';
import { AssetUrlPipe } from '../../../shared/pipes/asset-url.pipe';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';

type activityAsset = {
  asset?: AssetDto;
  item: ActivityItem;
  loaded?: boolean;
  title?: string;
  galleryId: string;
  assetId: string;
};
@Component({
  selector: 'app-activity-assets-dialog',
  templateUrl: './activity-assets-dialog.component.html',
  styleUrls: ['./activity-assets-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SvgComponent,
    ScrollElementDirective,
    InViewDirective,
    LinkListItemComponent,
    ActivityBadgeComponent,
    DatePipe,
    CapitalizePipe,
    AssetUrlPipe,
    AsyncPipe,
  ],
})
export class ActivityAssetsDialogComponent implements OnGetData {
  private activityService = inject(ActivityService);
  private assetService = inject(AssetService);
  private check = inject(ChangeDetectorRef);
  dialogName = 'activity-assets';
  group: ActivityGroup;
  word: string;
  items: activityAsset[];

  dialogOptions(): DialogOptions {
    return { size: 480, spacingX: 60, spacingY: 40, hasSmallBottomPart: true };
  }

  async onGetData(data: { group: ActivityGroup }) {
    this.group = data.group;
    this.word = this.activityService.getWord(this.group.type, 'tenant');
    if (this.group.type === 'multi-asset-download.created') {
      this.items = this.group.assetIds.map(assetId => {
        const item = this.group.items[0];
        return {
          item: item,
          galleryId: item.context.gallery?.id ?? item.context.deletedGalleryId,
          assetId: assetId,
        };
      });
    } else {
      this.items = this.group.items.map(item => {
        return {
          item: item,
          galleryId: item.context.gallery?.id ?? item.context.deletedGalleryId,
          assetId: item.context.assetId,
        };
      });
    }
    this.check.detectChanges();
    if (this.items.length > 10) {
      // load all gallery assets so that we don't do so many requests
      const galleryId = this.items.find(Boolean)?.galleryId;
      if (!galleryId) {
        this.items.forEach(item => {
          item.loaded = true;
          item.title = 'Unknown';
        });
      }
      const assets = await this.loadAssets(galleryId);
      if (!assets) {
        // if we cant get the assets, then we can assume that all of them are deleted (because the gallery is deleted)
        this.items.forEach(item => {
          item.loaded = true;
          item.title = 'Deleted asset';
        });
        this.check.detectChanges();
        return;
      }
    }
    this.items.forEach(item => (item.title = 'Loading...'));

    const resps = await Promise.all(
      this.items.map(item => {
        return this.loadAsset(item.assetId, item.galleryId);
      })
    );

    this.items.forEach((item, i) => {
      const resp = resps[i];
      item.asset = resp;
      item.loaded = true;
      item.title = resp ? resp.version_data.asset_name : 'Deleted asset';
    });
    this.check.detectChanges();
  }

  private async loadAsset(assetId: string, galleryId: string) {
    try {
      return await this.assetService.getAsset(assetId, galleryId);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 404 && err.status !== 403 && err.status !== 401) throw err;
    }
  }

  private async loadAssets(galleryId: string) {
    try {
      return await this.assetService.getAssetsForGallery(galleryId);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 404 && err.status !== 403 && err.status !== 401) throw err;
    }
  }
}
